import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import clsx from "clsx";
import { useEffect, useState } from "react";

import { Icon } from "../Icon";

import { countryCodeOptions, extractCountryCode, npaLookup } from "./utils";

export default function PhoneInput({
  countryId,
  defaultValue,
  onChange,
  disabled = false,
  invalid,
}: {
  countryId?: string;
  defaultValue?: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange?: (phoneNumber: string) => void;
}) {
  const [countryCode, setCountryCode] = useState<{
    code: string;
    id: string;
    max: number;
    min: number;
    name: string;
  }>(countryCodeOptions["us"]);
  const [phoneNumber, setPhoneNumber] = useState("");

  const sortedCountryCodeOptions = Object.values(countryCodeOptions).sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  useEffect(() => {
    if (defaultValue) {
      const countryCode = extractCountryCode(defaultValue);
      const usCode = countryCodeOptions["us"];
      if (!countryCode) {
        setCountryCode(usCode);
        setPhoneNumber(defaultValue.replace(usCode.code, ""));
        return;
      }

      if (countryCode) {
        // Special handling for NANP numbers to determine country based on NPA
        if (countryCode === "+1") {
          const npa = defaultValue.slice(2, 5) as keyof typeof npaLookup;
          const countryAccordingToNpa = npaLookup[npa];
          if (countryAccordingToNpa) {
            for (const option of Object.values(countryCodeOptions)) {
              if (option.id === countryAccordingToNpa.id) {
                setCountryCode(option);
                setPhoneNumber(
                  defaultValue.replace(countryAccordingToNpa.code, ""),
                );
                break;
              }
            }
            return;
          } else {
            setCountryCode(usCode);
            setPhoneNumber(defaultValue.replace(usCode.code, ""));
            return;
          }
        }

        for (const option of Object.values(countryCodeOptions)) {
          if (option.code === countryCode) {
            setCountryCode(option);
            setPhoneNumber(defaultValue.replace(countryCode, ""));
            break;
          }
        }
      }
    }
  }, [defaultValue]);

  useEffect(() => {
    if (countryId) {
      const id = countryId.toLowerCase();
      const option = countryCodeOptions[id as keyof typeof countryCodeOptions];
      if (option) {
        setCountryCode(option);
        if (phoneNumber && onChange) {
          onChange(`${option.code}${phoneNumber}`);
        }
      }
    }
  }, [countryId]);

  const handleOnChange = (cc: typeof countryCode, number: string) => {
    setCountryCode(cc);
    setPhoneNumber(number);

    const newPhoneNumber = `${cc.code}${number}`;

    if (onChange) {
      onChange(newPhoneNumber);
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    // Remove all non-digit characters except plus sign
    const cleanedNumber = pastedText.replace(/[^\d+]/g, "");

    if (cleanedNumber.startsWith("+")) {
      const countryCode = extractCountryCode(cleanedNumber);

      if (countryCode) {
        // If country code exists, remove it from number to get the actual number
        const formattedNumber = cleanedNumber.replace(countryCode, "");

        if (countryCode === "+1") {
          const npa = cleanedNumber.slice(2, 5) as keyof typeof npaLookup;
          const countryAccordingToNpa = npaLookup[npa];
          if (countryAccordingToNpa) {
            for (const option of Object.values(countryCodeOptions)) {
              if (option.id === countryAccordingToNpa.id) {
                handleOnChange(option, formattedNumber);
                break;
              }
            }
          } else {
            // If the NPA number does not match, assume the number as US number.
            handleOnChange(countryCodeOptions["us"], formattedNumber);
          }
        } else {
          for (const option of Object.values(countryCodeOptions)) {
            if (option.code === countryCode) {
              handleOnChange(option, formattedNumber);
              break;
            }
          }
        }
      } else {
        // If no country code available, remove the '+' sign and use US country code.
        const formattedNumber = cleanedNumber.replace("+", "");
        handleOnChange(countryCodeOptions["us"], formattedNumber);
      }
    } else {
      // If no '+' sign in the cleaned number, use US as country code and cleaned number as actual number.
      handleOnChange(countryCodeOptions["us"], cleanedNumber);
    }
  };

  return (
    <div
      className={clsx(
        "flex items-center text-xs lg:text-sm w-full divide-x text-gray-500 border rounded-md",

        invalid ? "border-red-300" : "border-gray-300",
      )}
    >
      <Listbox
        value={countryCode}
        onChange={(value) => handleOnChange(value, phoneNumber)}
        disabled={disabled}
      >
        <ListboxButton className="flex relative w-[105px] h-[36px] px-2 py-1 ">
          {({ open }) => (
            <span className="flex justify-between items-center w-full text-xs lg:text-sm">
              <span className="flex items-center gap-2">
                <img
                  className="w-5"
                  src={`/images/flags/${countryCode?.id}.webp`}
                  alt={countryCode?.name}
                />
                <span>{countryCode?.code}</span>
              </span>
              <span className="ml-auto">
                <Icon
                  name="fasChevronDown"
                  className={clsx(
                    "w-3 transition-transform duration-200",
                    open && "transform rotate-180",
                  )}
                />
              </span>
            </span>
          )}
        </ListboxButton>

        <ListboxOptions
          anchor={{
            gap: 2,
            to: "bottom",
          }}
          transition
          className=" rounded-md z-50 left-0 h-80 shadow-lg absolute w-64 divide-y overflow-y-auto bg-white origin-top border transition duration-1000 ease-out empty:invisible data-closed:scale-95 data-closed:opacity-0"
        >
          {sortedCountryCodeOptions.map((option) => (
            <ListboxOption
              className="cursor-pointer w-full "
              key={option.code + option.id}
              value={option}
            >
              {({ selected }) => {
                return (
                  <div className="flex items-center justify-between text-xs lg:text-sm text-gray-500 px-2 py-2 hover:bg-gray-100">
                    <span className="flex items-center gap-2">
                      <img
                        className="w-6 rounded-sm"
                        src={`/images/flags/${option.id}.webp`}
                      />
                      <span>
                        {option.name} ({option.code})
                      </span>
                    </span>
                    {selected && (
                      <Icon name="circleCheck" className="w-3 lg:w-4" />
                    )}
                  </div>
                );
              }}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
      <input
        type="tel"
        onChange={(e) => handleOnChange(countryCode, e.target.value)}
        value={phoneNumber}
        className="h-[36px] w-[calc(100%-105px)] rounded-tr-md rounded-br-md  focus:outline-none active:ring-0 px-2 py-1"
        disabled={disabled}
        onPaste={(e) => handleOnPaste(e)}
      />
    </div>
  );
}
